import React from 'react';
import { useNavigate } from 'react-router-dom';

import {
  ChevronDownIcon,
  EnvelopeIcon,
  PhoneIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { Button } from '@oysterjs/ui/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle
} from '@oysterjs/ui/dialog';
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '@oysterjs/ui/dropdown';
import { Description, Field, FieldGroup, Fieldset, Label } from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import { Textarea } from '@oysterjs/ui/textarea';

import {
  FileInput,
  emptyFileInputChange,
  hasFileInputContent,
  type FileInputChange
} from '~/components/file';
import { FormError } from '~/components/form-error';
import {
  CREATE_CARA_WORKSPACE,
  CREATE_OUTBOUND_CALL,
  usePermissions
} from '~/components/permissions';
import { useOutboundCallsEnabled } from '~/components/use-outbound-calls-enabled';
import { useDefaultVisibilityValue } from '~/components/visibility/default-visibility';
import { useCreateCaraWorkspace } from '~/components/workspaces/use-create-cara-workspace';
import {
  type OutboundCallDraftPrefill,
  outboundCallPrefillSearch
} from '~/components/calls/outbound-call';

/** The subset of a client the actions read. */
export type ClientActionTarget = {
  id: string;
  name: string;
  email?: string | null;
  phone?: string | null;
};

/**
 * A call goes to the Calls > Outbound form rather than a dialog here, because
 * that form owns the agent choice, scheduling and validation.
 */
export const ClientActionsMenu = ({ client }: { client: ClientActionTarget }) => {
  const navigate = useNavigate();
  const { hasAllPermissions } = usePermissions();
  const outboundCallsEnabled = useOutboundCallsEnabled();
  const [dialog, setDialog] = React.useState<'workspace' | 'email' | null>(null);

  const canCreateWorkspace = hasAllPermissions(CREATE_CARA_WORKSPACE);
  const canScheduleCall = outboundCallsEnabled && hasAllPermissions(CREATE_OUTBOUND_CALL);
  if (!canCreateWorkspace && !canScheduleCall) {
    return null;
  }

  const scheduleCall = () =>
    navigate(`/app/calls/outbound/new${outboundCallPrefillSearch(prefillForClient(client))}`);

  return (
    <>
      <Dropdown>
        <DropdownButton color="sky">
          New for this client
          <ChevronDownIcon data-slot="icon" />
        </DropdownButton>
        <DropdownMenu anchor="bottom end">
          {canCreateWorkspace && (
            <>
              <DropdownItem onClick={() => setDialog('workspace')}>
                <SparklesIcon data-slot="icon" />
                Create a workspace
              </DropdownItem>
              <DropdownItem onClick={() => setDialog('email')}>
                <EnvelopeIcon data-slot="icon" />
                Draft an email
              </DropdownItem>
            </>
          )}
          {canScheduleCall && (
            <DropdownItem onClick={scheduleCall}>
              <PhoneIcon data-slot="icon" />
              Schedule an outbound call
            </DropdownItem>
          )}
        </DropdownMenu>
      </Dropdown>

      <Dialog open={dialog === 'workspace'} onClose={() => setDialog(null)} size="2xl">
        <ClientWorkspaceForm client={client} onClose={() => setDialog(null)} />
      </Dialog>
      <Dialog open={dialog === 'email'} onClose={() => setDialog(null)} size="2xl">
        <ClientEmailForm client={client} onClose={() => setDialog(null)} />
      </Dialog>
    </>
  );
};

export const prefillForClient = (client: ClientActionTarget): OutboundCallDraftPrefill => ({
  customerName: client.name,
  customerPhone: client.phone ?? ''
});

/** Cara needs an opening message even when the user only attached files. */
export const workspaceRequestForClient = (notes: string, clientName: string): string =>
  notes.trim() || `Review the attached files for ${clientName}.`;

/** Recipient and subject ride in the message: the create input has no field for them. */
export const emailDraftRequest = (input: {
  clientName: string;
  to: string;
  subject: string;
  instructions: string;
}): string => {
  const to = input.to.trim();
  const subject = input.subject.trim();
  return [
    `Draft an email to ${to ? `${input.clientName} (${to})` : input.clientName}.`,
    subject && `Subject: ${subject}`,
    input.instructions.trim()
  ]
    .filter(Boolean)
    .join('\n');
};

type ClientActionFormProps = {
  client: ClientActionTarget;
  onClose: () => void;
};

/** The create hook navigates to the new workspace, so a form never closes itself. */
const useClientWorkspaceLauncher = (client: ClientActionTarget) => {
  const { createWorkspace, error } = useCreateCaraWorkspace(useDefaultVisibilityValue());
  const [submitting, setSubmitting] = React.useState(false);

  const launch = async (message: string, attached = emptyFileInputChange()) => {
    setSubmitting(true);
    try {
      await createWorkspace(message, attached.files, {
        clientId: client.id,
        amsContext: attached.amsContext,
        amsRefs: attached.amsRefs
      });
    } catch {
      // Already recorded in `error` by the create hook.
    } finally {
      setSubmitting(false);
    }
  };

  return { launch, submitting, error };
};

/**
 * Lives inside `Dialog`, which unmounts it on close, so each opening starts from
 * empty fields and no error.
 */
const ClientActionForm = (props: {
  title: React.ReactNode;
  description: React.ReactNode;
  submitLabel: string;
  canSubmit: boolean;
  submitting: boolean;
  error?: string;
  onSubmit: () => void;
  onClose: () => void;
  children: React.ReactNode;
}) => (
  <>
    <DialogTitle>{props.title}</DialogTitle>
    <DialogDescription>{props.description}</DialogDescription>
    <DialogBody>
      <Fieldset disabled={props.submitting}>
        <FieldGroup>
          {props.children}
          <FormError message={props.error} />
        </FieldGroup>
      </Fieldset>
    </DialogBody>
    <DialogActions>
      <Button plain onClick={props.onClose} disabled={props.submitting}>
        Cancel
      </Button>
      <Button
        color="sky"
        loading={props.submitting}
        disabled={!props.canSubmit}
        onClick={props.onSubmit}
      >
        {props.submitLabel}
      </Button>
    </DialogActions>
  </>
);

const ClientWorkspaceForm = ({ client, onClose }: ClientActionFormProps) => {
  const [notes, setNotes] = React.useState('');
  const [attached, setAttached] = React.useState<FileInputChange>(emptyFileInputChange());
  const { launch, submitting, error } = useClientWorkspaceLauncher(client);

  return (
    <ClientActionForm
      title={`New workspace for ${client.name}`}
      description="The workspace is filed under this client, so Cara starts from their policies, documents, and earlier work."
      submitLabel="Create workspace"
      canSubmit={notes.trim().length > 0 || hasFileInputContent(attached)}
      submitting={submitting}
      error={error?.message}
      onClose={onClose}
      onSubmit={() => void launch(workspaceRequestForClient(notes, client.name), attached)}
    >
      <Field>
        <Label>Files</Label>
        <Description>Quotes, policies, or anything else Cara should read. Optional.</Description>
        <FileInput multiple enableExisting enableAMSSearch onChange={setAttached} />
      </Field>
      <Field>
        <Label>What should Cara do?</Label>
        <Textarea
          rows={3}
          value={notes}
          placeholder="Compare the renewal offer against the expiring policy."
          onChange={(e) => setNotes(e.currentTarget.value)}
        />
      </Field>
    </ClientActionForm>
  );
};

const ClientEmailForm = ({ client, onClose }: ClientActionFormProps) => {
  const [to, setTo] = React.useState(client.email ?? '');
  const [subject, setSubject] = React.useState('');
  const [instructions, setInstructions] = React.useState('');
  const { launch, submitting, error } = useClientWorkspaceLauncher(client);

  return (
    <ClientActionForm
      title={`Draft an email to ${client.name}`}
      description={
        <>
          Cara drafts from this client&rsquo;s policies, documents, and recent work. You review and
          send.
        </>
      }
      submitLabel="Draft with Cara"
      canSubmit={instructions.trim().length > 0}
      submitting={submitting}
      error={error?.message}
      onClose={onClose}
      onSubmit={() =>
        void launch(emailDraftRequest({ clientName: client.name, to, subject, instructions }))
      }
    >
      <Field>
        <Label>To</Label>
        <Input type="email" value={to} onChange={(e) => setTo(e.currentTarget.value)} />
      </Field>
      <Field>
        <Label>Subject</Label>
        <Input
          value={subject}
          placeholder="Your 2026 general liability renewal"
          onChange={(e) => setSubject(e.currentTarget.value)}
        />
      </Field>
      <Field>
        <Label>What should the email say?</Label>
        <Description>A sentence is enough. Cara fills in the coverage detail.</Description>
        <Textarea
          rows={3}
          value={instructions}
          placeholder="Walk them through the renewal comparison and flag the premium increase."
          onChange={(e) => setInstructions(e.currentTarget.value)}
        />
      </Field>
    </ClientActionForm>
  );
};
